import React, { useState } from "react";
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Alert,
  Row,
  Col,
  InputGroup,
  Tabs,
  Tab,
} from "react-bootstrap";
import ReactQuill from "react-quill-new"; // Import Library
import "react-quill-new/dist/quill.snow.css"; // Import Styles
import PhotoUpload from "./PhotoUpload";
import VideoUpload from "./VideoUpload";

const PropertyManagement = ({
  properties,
  onAddProperty,
  onUpdateProperty,
  onDeleteProperty,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // State with capacity fields
  const [formData, setFormData] = useState({
    title: "",
    address: "",
    location: "",
    headline: "",
    details: "", // HTML content
    guests: "",
    bedrooms: "",
    beds: "",
    baths: "",
    bookingUrl: "",
    icalUrl: "",
    hospitableId: "", // New Field
    videoUrl: "",
    videoFile: null,
    image: "",
    featured: false,
    photos: [],
    slug: "",
  });

  // Configure Quill Toolbar
  const modules = {
    toolbar: [
      [{ header: [2, 3, false] }], // H2, H3, Normal
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      ["clean"], // Remove formatting
    ],
  };

  const formats = ["header", "bold", "italic", "underline", "list"];

  const resetForm = () => {
    setFormData({
      title: "",
      address: "",
      location: "",
      headline: "",
      details: "",
      guests: "",
      bedrooms: "",
      beds: "",
      baths: "",
      bookingUrl: "",
      icalUrl: "",
      hospitableId: "", // Reset New Field
      videoUrl: "",
      videoFile: null,
      image: "",
      featured: false,
      photos: [],
      slug: "",
    });
    setEditingProperty(null);
    setError("");
  };

  // Helper to clean pasted text
  const cleanHtml = (html) => {
    if (!html) return "";
    // Replace &nbsp; with regular spaces
    return html.replace(/&nbsp;/g, " ");
  };

  const handlePhotosUpdate = (photos) => {
    setFormData((prev) => ({
      ...prev,
      photos,
      image: photos.length > 0 ? photos[0].url : prev.image,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setLoading(true);
    setError("");

    try {
      if (!formData.title?.trim()) {
        throw new Error("Property name is required");
      }

      let cleanSlug = formData.slug
        ? formData.slug
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "-")
            .replace(/[^a-z0-9-]/g, "")
        : "";

      const submitData = {
        title: formData.title.trim(),
        address: formData.address || "",
        location: formData.location || "",
        headline: formData.headline || "",
        details: cleanHtml(formData.details || ""),
        guests: formData.guests || 0,
        bedrooms: formData.bedrooms || 0,
        beds: formData.beds || 0,
        baths: formData.baths || 0,
        bookingUrl: formData.bookingUrl || "",
        icalUrl: formData.icalUrl || "",
        hospitableId: formData.hospitableId || "", // Save New Field
        videoUrl: formData.videoUrl || "",
        videoFile: formData.videoFile || null,
        image: formData.image || "",
        featured: formData.featured || false,
        photos: formData.photos || [],
        slug: cleanSlug,
      };

      if (editingProperty) {
        await onUpdateProperty(editingProperty.id, submitData);
      } else {
        await onAddProperty(submitData);
      }

      setShowModal(false);
      resetForm();
    } catch (err) {
      console.error("Submit error:", err);
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      title: property.title || "",
      address: property.address || "",
      location: property.location || "",
      headline: property.headline || "",
      details: property.details || "",
      guests: property.guests || "",
      bedrooms: property.bedrooms || "",
      beds: property.beds || "",
      baths: property.baths || "",
      bookingUrl: property.bookingUrl || "",
      icalUrl: property.icalUrl || "",
      hospitableId: property.hospitableId || "", // Load New Field
      videoUrl: property.videoUrl || "",
      videoFile: property.videoFile || null,
      image: property.image || "",
      featured: property.featured || false,
      photos: property.photos || [],
      slug: property.slug || "",
    });
    setShowModal(true);
  };

  const handleDelete = async (id, propertyName) => {
    if (window.confirm(`Are you sure you want to delete "${propertyName}"?`)) {
      try {
        await onDeleteProperty(id);
      } catch (err) {
        alert("Failed to delete property: " + err.message);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
    setError("");
    setLoading(false);
  };

  const handleOpenAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleVideoUpdate = (videoData) => {
    setFormData((prev) => ({
      ...prev,
      videoFile: videoData,
      videoUrl: videoData ? "" : prev.videoUrl,
    }));
  };

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Property Management</h5>
          <Button
            variant="primary"
            onClick={handleOpenAddModal}
            disabled={loading}
          >
            Add Property
          </Button>
        </Card.Header>
        <Card.Body>
          {properties.length === 0 ? (
            <div className="text-center py-4">
              <p className="text-muted mb-3">No properties added yet.</p>
              <Button
                variant="outline-primary"
                onClick={handleOpenAddModal}
                disabled={loading}
              >
                Add Your First Property
              </Button>
            </div>
          ) : (
            <Table responsive striped>
              <thead>
                <tr>
                  <th>Property Name</th>
                  <th>Location</th>
                  <th>Capacity</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((property) => (
                  <tr key={property.id}>
                    <td>
                      <strong>{property.title}</strong>
                    </td>
                    <td>{property.location || "-"}</td>
                    <td>
                      <small className="text-muted">
                        {property.guests} Guests • {property.bedrooms} BR •{" "}
                        {property.baths} BA
                      </small>
                    </td>
                    <td>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        className="me-2"
                        onClick={() => handleEdit(property)}
                        disabled={loading}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() =>
                          handleDelete(property.id, property.title)
                        }
                        disabled={loading}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingProperty ? "Edit Property" : "Add Property"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit} noValidate>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}

            <Tabs defaultActiveKey="general" className="mb-4">
              <Tab eventKey="general" title="General Info">
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Property Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.title}
                        onChange={(e) =>
                          setFormData({ ...formData, title: e.target.value })
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Address</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.address}
                        onChange={(e) =>
                          setFormData({ ...formData, address: e.target.value })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Location</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.location}
                        onChange={(e) =>
                          setFormData({ ...formData, location: e.target.value })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <h6 className="mt-2 mb-3">Capacity & Rooms</h6>
                <Row className="mb-3">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Guests</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.guests}
                        onChange={(e) =>
                          setFormData({ ...formData, guests: e.target.value })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Bedrooms</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.bedrooms}
                        onChange={(e) =>
                          setFormData({ ...formData, bedrooms: e.target.value })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Beds</Form.Label>
                      <Form.Control
                        type="number"
                        value={formData.beds}
                        onChange={(e) =>
                          setFormData({ ...formData, beds: e.target.value })
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Baths</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.5"
                        value={formData.baths}
                        onChange={(e) =>
                          setFormData({ ...formData, baths: e.target.value })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Custom URL Slug</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>acfluxurystays.com/homes/</InputGroup.Text>
                    <Form.Control
                      type="text"
                      value={formData.slug}
                      onChange={(e) =>
                        setFormData({ ...formData, slug: e.target.value })
                      }
                      placeholder="property-name"
                    />
                  </InputGroup>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Featured Property"
                    checked={formData.featured}
                    onChange={(e) =>
                      setFormData({ ...formData, featured: e.target.checked })
                    }
                  />
                </Form.Group>
              </Tab>

              <Tab eventKey="details" title="Details & Links">
                <Form.Group className="mb-3">
                  <Form.Label>Headline</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.headline}
                    onChange={(e) =>
                      setFormData({ ...formData, headline: e.target.value })
                    }
                    placeholder="Short catchy description"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Full Description</Form.Label>
                  <ReactQuill
                    theme="snow"
                    value={formData.details}
                    onChange={(content) =>
                      setFormData({ ...formData, details: content })
                    }
                    modules={modules}
                    formats={formats}
                    style={{
                      height: "300px",
                      marginBottom: "50px",
                      overflowWrap: "break-word",
                      wordWrap: "break-word",
                      wordBreak: "keep-all",
                    }}
                  />
                </Form.Group>

                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Hospitable Property ID</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.hospitableId}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            hospitableId: e.target.value,
                          })
                        }
                        placeholder="e.g. 1687306"
                      />
                      <Form.Text className="text-muted">
                        The numeric ID from the widget code.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Booking URL (External)</Form.Label>
                      <Form.Control
                        type="url"
                        value={formData.bookingUrl}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            bookingUrl: e.target.value,
                          })
                        }
                        placeholder="Optional fallback link"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>iCal URL</Form.Label>
                      <Form.Control
                        type="url"
                        value={formData.icalUrl}
                        onChange={(e) =>
                          setFormData({ ...formData, icalUrl: e.target.value })
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Tab>

              <Tab eventKey="media" title="Photos & Video">
                <div className="mb-4">
                  <h6 className="mb-3">Property Photos</h6>
                  <PhotoUpload
                    propertyId={editingProperty ? editingProperty.id : "new"}
                    photos={formData.photos}
                    onPhotosUpdate={handlePhotosUpdate}
                  />
                </div>
                <hr />
                <div className="mb-3">
                  <h6 className="mb-3">Property Video</h6>
                  <VideoUpload
                    propertyId={editingProperty ? editingProperty.id : "new"}
                    video={formData.videoFile}
                    onVideoUpdate={handleVideoUpdate}
                    disabled={loading || !!formData.videoUrl}
                  />
                </div>
              </Tab>
            </Tabs>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading
                ? "Saving..."
                : editingProperty
                  ? "Update Property"
                  : "Add Property"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default PropertyManagement;
